import React, { useCallback, useEffect, useMemo, useState } from "react";
import CampaignTable from "@/features/campaigns/components/UserCampaigns/components/CampaignTable";
import { useGetCampaigns } from "@/features/campaigns/hooks/useCampaign";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { renderCampaignCell } from "@/shared/utils/campaignConfig";
import Loader from "@/shared/components/ui/Loaders/Loader";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import PaginationInfo from "@/shared/components/ui/Pagination/PaginationInfo";
import NoDataTable from "@/shared/components/ui/NoDataView/NoDataTable";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import { X } from "lucide-react";
const ROWS_PER_PAGE = 15;
const MODAL_TABLE_HEIGHT = "min(500px, 55vh)";

const CampaignsListModal = ({
  isOpen,
  onClose,
  contactsToAdd = [],
  moveMode,
  onCampaignSelected,
  isScoring = false,
  isMoving = false,
}) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);

  const { data: campaignsData, isLoading } = useGetCampaigns(
    page,
    ROWS_PER_PAGE,
    searchValue,
    isOpen,
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
      setPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    setPage(1);
  }, [searchValue]);

  const displayData = useMemo(
    () =>
      campaignsData?.campaigns?.map((campaign) => ({
        id: campaign?.id,
        name: campaign?.name || campaign?.campaignName || "",
        created_on: campaign?.created_at
          ? new Date(campaign?.created_at).toLocaleDateString()
          : "",
        sent: campaign?.sent_count ?? campaign?.sent ?? 0,
        clicked: campaign?.clicked_count ?? campaign?.clicked ?? 0,
        opened: campaign?.opened_count ?? campaign?.opened ?? 0,
        status: campaign?.status || "Draft",
      })) ?? [],
    [campaignsData],
  );

  const totalCount = campaignsData?.totalCount ?? 0;

  const handleRowClick = useCallback(
    async (campaign) => {
      console.info("[MoveCampaign][campaign-click]", {
        campaignId: campaign?.id,
        contactsCount: contactsToAdd.length,
        isMoving,
        isScoring,
        hasSelectionHandler: typeof onCampaignSelected === "function",
      });
      if (
        !campaign?.id ||
        contactsToAdd.length === 0 ||
        isMoving ||
        isScoring ||
        typeof onCampaignSelected !== "function"
      ) {
        console.info("[MoveCampaign][campaign-click-blocked]");
        return;
      }
      await onCampaignSelected(campaign);
    },
    [contactsToAdd.length, isMoving, isScoring, onCampaignSelected],
  );

  const renderCell = useCallback(
    (item, columnKey) => renderCampaignCell(item, columnKey, null, null),
    [],
  );

  const handleNewCampaign = useCallback(() => {
    navigate("/campaigns");
  }, [navigate]);

  const handleCloseModal = useCallback(() => {
    if (isMoving || isScoring) return;
    onClose();
    setSearchValue("");
    setPage(1);
  }, [isMoving, isScoring, onClose, setSearchValue, setPage]);

  const loadingText =
    isScoring
      ? "Generating match scores for selected leads..."
      : moveMode === "both"
      ? "Adding leads and generating account information..."
      : "Adding leads and generating profiles...";

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      width="900px"
      className="p-6"
    >
      <div className="relative max-h-[calc(100dvh-100px)]">
        {(isMoving || isScoring) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 rounded-lg">
            <Loader text={loadingText} />
          </div>
        )}
        {!isMoving && isLoading && displayData.length === 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 rounded-lg">
            <Loader text="Loading campaigns..." />
          </div>
        )}
        {displayData.length > 0 ? (
          <div className="flex flex-col gap-2.5 h-full w-full">
            <div className="flex justify-between items-center">
              <div className="flex flex-col">
                <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Campaigns</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Select a campaign to add leads to
                </p>
              </div>
              <button
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                onClick={handleCloseModal}
                disabled={isMoving || isScoring}
              >
                <X size={20} className="text-gray-800 dark:text-gray-200" />
              </button>
            </div>
            <div className="flex items-center justify-between gap-4">
              <SearchBar
                value={searchValue}
                setFilterValue={setSearchValue}
                onChange={setSearchValue}
                placeholder="Search campaigns..."
                className="w-full max-w-[280px]"
              />
              <PaginationInfo
                totalCount={totalCount}
                currentPage={page}
                rowsPerPage={ROWS_PER_PAGE}
                itemName="campaign"
              />
            </div>
            <CampaignTable
              displayData={displayData}
              totalCount={totalCount}
              isLoading={isLoading}
              searchValue={searchValue}
              rowsPerPage={ROWS_PER_PAGE}
              setRowsPerPage={() => {}}
              renderCell={renderCell}
              enableSelection={false}
              onRowClick={handleRowClick}
              selectedCampaigns={[]}
              setSelectedCampaigns={() => {}}
              onSelectionChange={() => {}}
              height={MODAL_TABLE_HEIGHT}
              page={page}
              onPageChange={setPage}
            />
          </div>
        ) : (
          <div className="w-full relative">
            <NoDataTable variant="campaigns" />
            <div className="flex flex-col gap-1.5 items-center justify-center absolute inset-0">
              <h3 className="text-gray-900 dark:text-gray-100 text-lg font-bold">
                No Campaigns Yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Start generating leads by creating your first campaign
              </p>
              <PrimaryButton
                className="mt-4"
                onClick={handleNewCampaign}
                startContent={true}
              >
                Go to Campaigns
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default CampaignsListModal;
